import type { OrganizationDocumentResponse } from '@acme/api/client';
import { OrganizationService } from '@acme/api/client';
import {
  FileUpload,
  type LoadedFile,
  type UploadingFile,
} from '@acme/foundation';
import { Card } from 'flowbite-react';
import { useEffect, useMemo, useState } from 'react';

export const OrganizationDocuments = () => {
  const [documents, setDocuments] = useState<OrganizationDocumentResponse[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);

  const fetchDocuments = async () => {
    try {
      setIsLoading(true);
      const response = await OrganizationService.getApiV1OrganizationFiles1({});
      if (response.isSuccess && response.data) {
        setDocuments(response.data);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const loadedFiles: LoadedFile[] = useMemo(() => {
    return documents.map((doc) => ({
      id: doc.id || '',
      name: doc.name || 'Без названия',
      uploadDate: doc.uploadDate,
      onDownload: doc.id
        ? async () => {
            try {
              const response =
                await OrganizationService.getApiV1OrganizationFiles({
                  fileId: doc.id!,
                });

              let blob: Blob;
              if (response instanceof Blob) {
                blob = response;
              } else if (typeof response === 'string') {
                blob = new Blob([response], { type: 'application/pdf' });
              } else {
                console.error(
                  'Unexpected response type:',
                  typeof response,
                  response
                );
                return;
              }

              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = doc.name || 'document';
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            } catch (error) {
              console.error('Error downloading document:', error);
            }
          }
        : undefined,
    }));
  }, [documents]);

  const handleFileSelect = async (files: File[]) => {
    const newUploadingFiles: UploadingFile[] = files.map((file, index) => ({
      id: `upload-${Date.now()}-${index}`,
      file,
      status: 'uploading',
      progress: 0,
    }));

    setUploadingFiles((prev) => [...prev, ...newUploadingFiles]);

    for (let i = 0; i < files.length; i++) {
      const uploadingFile = newUploadingFiles[i];
      try {
        const fileBlobs = [files[i]];

        const simulateProgress = () => {
          return new Promise<void>((resolve) => {
            let progress = 0;
            const interval = setInterval(() => {
              progress += Math.random() * 30;
              if (progress > 90) progress = 90;

              setUploadingFiles((prev) =>
                prev.map((uf) =>
                  uf.id === uploadingFile.id
                    ? { ...uf, progress: Math.round(progress) }
                    : uf
                )
              );

              if (progress >= 90) {
                clearInterval(interval);
                resolve();
              }
            }, 200);
          });
        };

        await simulateProgress();

        await OrganizationService.postApiV1OrganizationFilesUpload({
          formData: {
            uploads: fileBlobs,
            documentTypeIds: [],
          },
        });

        setUploadingFiles((prev) =>
          prev.map((uf) =>
            uf.id === uploadingFile.id
              ? { ...uf, status: 'completed', progress: 100 }
              : uf
          )
        );

        await fetchDocuments();

        setTimeout(() => {
          setUploadingFiles((prev) =>
            prev.filter((uf) => uf.id !== uploadingFile.id)
          );
        }, 2000);
      } catch (error) {
        console.error('Error uploading document:', error);
        setUploadingFiles((prev) =>
          prev.map((uf) =>
            uf.id === uploadingFile.id
              ? {
                  ...uf,
                  status: 'error',
                  error:
                    error instanceof Error ? error.message : 'Ошибка загрузки',
                }
              : uf
          )
        );
      }
    }
  };

  const handleFileRemove = async (fileId: string) => {
    setUploadingFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleFileResume = (fileId: string) => {
    setUploadingFiles((prev) =>
      prev.map((uf) => (uf.id === fileId ? { ...uf, status: 'uploading' } : uf))
    );
  };

  return (
    <Card>
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Документы организации</h2>
      </div>

      <FileUpload
        onFileSelect={handleFileSelect}
        onFileRemove={handleFileRemove}
        onFileResume={handleFileResume}
        accept=".pdf"
        maxSize={10}
        multiple={true}
        uploadingFiles={uploadingFiles}
        loadedFiles={loadedFiles}
      />
    </Card>
  );
};
